import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Brain, Plus, Trash2, Check, X, Edit3, Sparkles } from "lucide-react";
import { toast } from "sonner";

const TONES = ["friendly", "bold", "authoritative", "warm", "playful"];
const STYLES = ["consultative", "urgency", "value-first", "story-led"];
const EMOJIS = ["none", "minimal", "playful"];

export default function Training() {
  const [profile, setProfile] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [stats, setStats] = useState(null);
  const [newQ, setNewQ] = useState("");
  const [newA, setNewA] = useState("");

  const loadAll = async () => {
    const [p, f, a, s] = await Promise.all([
      api.get("/training/profile"),
      api.get("/training/faqs"),
      api.get("/training/approvals"),
      api.get("/training/stats"),
    ]);
    setProfile(p.data); setFaqs(f.data); setApprovals(a.data); setStats(s.data);
  };
  useEffect(() => { loadAll(); }, []);

  const saveProfile = async () => {
    const r = await api.put("/training/profile", profile);
    setProfile(r.data);
    toast.success("AI tone updated");
  };

  const addFaq = async () => {
    if (!newQ.trim() || !newA.trim()) return;
    const r = await api.post("/training/faqs", { question: newQ, answer: newA });
    setFaqs([...faqs, r.data]);
    setNewQ(""); setNewA("");
    toast.success("FAQ taught to AI");
    loadAll();
  };

  const delFaq = async (id) => {
    await api.delete(`/training/faqs/${id}`);
    setFaqs(faqs.filter((f) => f.id !== id));
  };

  const decide = async (id, decision) => {
    await api.post(`/training/approvals/${id}`, { decision });
    setApprovals(approvals.filter((a) => a.id !== id));
    toast.success(`Reply ${decision}`);
    loadAll();
  };

  if (!profile || !stats) return <div className="p-10 text-zinc-400">Loading AI Training Center…</div>;

  return (
    <div className="min-h-screen bg-[#FAFAFA] p-6 lg:p-10" data-testid="training-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 grid place-items-center">
          <Brain className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">AI Training Center</h1>
          <p className="text-sm text-zinc-500 mt-1">Teach Ashra your tone, FAQs, and escalation rules. The more you train, the better it sells.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Tone strength" value={`${Math.round(stats.tone_strength * 100)}%`} tone="violet" />
        <Stat label="FAQs taught" value={stats.faqs_count} tone="emerald" />
        <Stat label="Decisions made" value={stats.decisions_total} tone="amber" />
        <Stat label="AI accuracy" value={`${Math.round(stats.ai_accuracy * 100)}%`} tone="rose" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 rounded-3xl bg-white border border-black/5 p-6">
          <h3 className="font-display text-sm font-bold mb-4 inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-600" /> Voice & sales style</h3>

          <Field label="Tone">
            <Pills options={TONES} value={profile.tone} onChange={(v) => setProfile({ ...profile, tone: v })} testid="tone-pill" />
          </Field>
          <Field label="Sales style">
            <Pills options={STYLES} value={profile.sales_style} onChange={(v) => setProfile({ ...profile, sales_style: v })} testid="style-pill" />
          </Field>
          <Field label="Emoji usage">
            <Pills options={EMOJIS} value={profile.emoji_usage} onChange={(v) => setProfile({ ...profile, emoji_usage: v })} testid="emoji-pill" />
          </Field>
          <Field label="Brand persona">
            <textarea
              data-testid="persona-input"
              className="w-full rounded-xl border border-black/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              rows={2}
              value={profile.persona || ""}
              onChange={(e) => setProfile({ ...profile, persona: e.target.value })}
            />
          </Field>
          <Field label="Escalation rules (one per line)">
            <textarea
              data-testid="escalation-rules-input"
              className="w-full rounded-xl border border-black/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 font-mono"
              rows={3}
              value={(profile.escalation_rules || []).join("\n")}
              onChange={(e) => setProfile({ ...profile, escalation_rules: e.target.value.split("\n").filter(Boolean) })}
            />
          </Field>
          <button onClick={saveProfile} className="mt-3 px-4 py-2 rounded-full ashra-gradient-bg text-white text-sm font-medium" data-testid="save-profile-btn">
            Save & retrain AI
          </button>
        </div>

        <div className="rounded-3xl bg-white border border-black/5 p-6">
          <h3 className="font-display text-sm font-bold mb-3">Approval queue · {approvals.length}</h3>
          <p className="text-xs text-zinc-500 mb-4">Review AI drafts to teach the right tone.</p>
          <div className="space-y-3 max-h-[460px] overflow-y-auto">
            {approvals.length === 0 && <p className="text-xs text-zinc-400 italic">All caught up · AI is learning from your past decisions.</p>}
            {approvals.map((a) => (
              <div key={a.id} className="rounded-2xl bg-zinc-50 p-3" data-testid={`approval-${a.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-medium">{a.lead_name}</p>
                  <span className="text-[10px] text-violet-600 font-bold">{Math.round((a.ai_confidence || 0) * 100)}%</span>
                </div>
                <p className="text-xs text-zinc-700 mb-3 italic">"{a.ai_draft}"</p>
                <div className="flex gap-2">
                  <button onClick={() => decide(a.id, "approved")} className="flex-1 text-xs px-2 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 inline-flex items-center justify-center gap-1" data-testid="approve-btn">
                    <Check className="h-3 w-3" /> Approve
                  </button>
                  <button onClick={() => decide(a.id, "rejected")} className="flex-1 text-xs px-2 py-1.5 rounded-lg bg-rose-100 text-rose-700 inline-flex items-center justify-center gap-1" data-testid="reject-btn">
                    <X className="h-3 w-3" /> Reject
                  </button>
                  <button onClick={() => decide(a.id, "edited")} className="flex-1 text-xs px-2 py-1.5 rounded-lg bg-amber-100 text-amber-700 inline-flex items-center justify-center gap-1">
                    <Edit3 className="h-3 w-3" /> Edit
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-3xl bg-white border border-black/5 p-6">
        <h3 className="font-display text-sm font-bold mb-4">FAQs · the brain Ashra answers from</h3>
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <input data-testid="faq-question-input" className="rounded-xl border border-black/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" placeholder="Question (e.g. Do you offer payment plans?)" value={newQ} onChange={(e) => setNewQ(e.target.value)} />
          <input data-testid="faq-answer-input" className="rounded-xl border border-black/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" placeholder="Answer the AI should give" value={newA} onChange={(e) => setNewA(e.target.value)} />
        </div>
        <button onClick={addFaq} className="px-4 py-2 rounded-full bg-zinc-900 text-white text-sm inline-flex items-center gap-1" data-testid="add-faq-btn">
          <Plus className="h-3 w-3" /> Teach AI
        </button>

        <ul className="mt-5 space-y-2">
          {faqs.map((f) => (
            <li key={f.id} className="flex items-start justify-between gap-3 py-3 border-b border-black/5 last:border-none">
              <div>
                <p className="text-sm font-medium">{f.question}</p>
                <p className="text-xs text-zinc-500 mt-0.5">{f.answer}</p>
              </div>
              <button onClick={() => delFaq(f.id)} className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-600" data-testid={`delete-faq-${f.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-4">
      <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">{label}</p>
      {children}
    </div>
  );
}

function Pills({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o}
          data-testid={`${testid}-${o}`}
          onClick={() => onChange(o)}
          className={`text-xs px-3 py-1.5 rounded-full border transition ${
            value === o ? "ashra-gradient-bg text-white border-transparent" : "border-black/10 hover:bg-zinc-50"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const toneCls = { emerald: "text-emerald-600", rose: "text-rose-600", violet: "text-violet-600", amber: "text-amber-600" }[tone] || "text-zinc-900";
  return (
    <div className="rounded-2xl bg-white border border-black/5 p-5">
      <p className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p className={`mt-1 font-display text-2xl font-extrabold ${toneCls}`}>{value}</p>
    </div>
  );
}
